import { useCallback, useMemo, useState } from 'react';

import { DATE_OPTIONS } from './scheduledMeetingsView.constants';

import { TDateTab } from './scheduledMeetingsView.types';
import { IScheduledMeeting, TToggleView } from '@src/types';

import { useFetchWebinarMeeting } from '@src/hooks';
import { useMediaQuery } from '@mui/material';

const useScheduledMeetings = () => {
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>('all');
  const [tempSelectedTypeFilter, setTempSelectedTypeFilter] = useState(selectedTypeFilter);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [view, setView] = useState<TToggleView>('list');
  const [selectedTab, setSelectedTab] = useState<TDateTab>('today');
  const [searchText, setSearchText] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [meetingLink, setMeetingLink] = useState<string>('');
  const [selectedMeeting, setSelectedMeeting] = useState<IScheduledMeeting | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date[] | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const { data, isLoading, error } = useFetchWebinarMeeting();
  const isMobile = useMediaQuery('(max-width:768px)');

  const onInvite = useCallback((meeting: IScheduledMeeting, link?: string) => {
    setSelectedMeeting(meeting);
    setIsModalOpen(true);
    if (link) setMeetingLink(link);
  }, []);

  const handleDateChange = (value: Date[] | null) => {
    setSelectedDate(value);
    if (value?.length) setSelectedTab('customDate');
  };

  const handleDatePickerToggle = (buttonRef: HTMLButtonElement | null) => {
    if (buttonRef) {
      const rect = buttonRef.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceNeeded = 400; // approximate height of calendar
      setDropdownPosition(spaceBelow < spaceNeeded ? 'top' : 'bottom');
    }
    setShowDatePicker((prev) => !prev);
  };

  const handleResetCustomDate = () => {
    setSelectedDate(null);
    setSelectedTab('today');
  };

  const onTabChange = (tab: string) => {
    setSelectedTab(tab as TDateTab);
    if (tab !== 'customDate') setSelectedDate(null);
  };

  const filterMeetingsByType = useMemo(
    () => (selectedTypeFilter === 'all' ? data : data?.filter((meeting) => meeting.type === selectedTypeFilter)),
    [selectedTypeFilter, data]
  );

  const searchedMeetings = useMemo(
    () => filterMeetingsByType?.filter(({ roomTitle }) => roomTitle.includes(searchText)),
    [filterMeetingsByType, searchText]
  );

  const filteredMeetings = useMemo(() => {
    return searchedMeetings?.filter((meeting) => {
      const now = new Date();
      if (selectedTab === 'today') {
        return (
          meeting.startTime >= new Date(now.setHours(0, 0, 0, 0)) &&
          meeting.startTime <= new Date(now.setHours(23, 59, 59, 999))
        );
      } else if (selectedTab === 'tomorrow') {
        const now = new Date();
        const start = new Date(now.setDate(now.getDate() + 1));
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setHours(23, 59, 59, 999);
        return meeting.startTime >= start && meeting.startTime <= end;
      } else if (selectedTab === 'later') {
        const later = new Date(new Date(now.setHours(0, 0, 0, 0)));
        later.setDate(later.getDate() + 2);
        return meeting.startTime >= later;
      } else if (selectedTab === 'customDate' && selectedDate) {
        const [start, end] = selectedDate;
        if (start && end) {
          return meeting.startTime >= start && meeting.startTime <= end;
        } else if (start) {
          return (
            meeting.startTime >= new Date(start.setHours(0, 0, 0, 0)) &&
            meeting.startTime <= new Date(start.setHours(23, 59, 59, 999))
          );
        }
      }
      return false;
    });
  }, [searchedMeetings, selectedTab, selectedDate]);

  const groupedMeetings: Record<string, IScheduledMeeting[]> = useMemo(
    () =>
      ['later', 'customDate'].includes(selectedTab)
        ? filteredMeetings
          ? filteredMeetings.reduce(
              (acc, meeting) => {
                const key = meeting.startTime.toLocaleDateString('en-US', DATE_OPTIONS);
                acc[key] = [...(acc[key] || []), meeting];
                return acc;
              },
              {} as Record<string, IScheduledMeeting[]>
            )
          : {}
        : {},
    [filteredMeetings, selectedTab]
  );

  return {
    isLoading,
    error,
    selectedTypeFilter,
    selectedTab,
    view,
    filteredMeetings,
    groupedMeetings,
    isMobile,
    setView,
    onTabChange,
    setSelectedTab,
    setSelectedTypeFilter,
    tempSelectedTypeFilter,
    setTempSelectedTypeFilter,
    isFilterModalOpen,
    setIsFilterModalOpen,
    isModalOpen,
    selectedDate,
    meetingLink,
    selectedMeeting,
    showDatePicker,
    dropdownPosition,
    setIsModalOpen,
    handleDateChange,
    setShowDatePicker,
    handleDatePickerToggle,
    handleResetCustomDate,
    setSelectedDate,
    onInvite,
  };
};

export default useScheduledMeetings;
